import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { ELLIPSIS_SX } from '../../utils.js';
import type { TagCategory } from '../data/api.js';
import { ChartSection } from '../shared/ChartSection.js';
import { CategoryLabel } from './CategoryLabel.js';
import { timelineCategories } from './tagTree.js';

const TRACK_LEFT_WIDTH = 120;

type TagFeature = TagCategory['features'][number];
type TagEntry = TagFeature['tags'][number];

export interface TagTimelineProps {
  durationSeconds: number;
  categories: TagCategory[];
}

function clampSpan(
  startSec: number,
  endSec: number,
  durationSeconds: number,
): { startSec: number; endSec: number } {
  const clampedStart = Math.max(0, Math.min(startSec, durationSeconds));
  const clampedEnd = Math.max(clampedStart, Math.min(endSec, durationSeconds));
  return { startSec: clampedStart, endSec: clampedEnd };
}

function TagRow({
  feature,
  tag,
  accent,
  durationSeconds,
}: {
  feature: TagFeature;
  tag: TagEntry;
  accent: string;
  durationSeconds: number;
}): ReactNode {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{ width: TRACK_LEFT_WIDTH, flexShrink: 0, minWidth: 0 }}>
        <Tooltip title={feature.feature} placement="top">
          <Typography sx={{ fontSize: 12, color: 'text.secondary', ...ELLIPSIS_SX }}>
            {feature.feature}
          </Typography>
        </Tooltip>
        <Tooltip title={tag.tag} placement="top">
          <Typography
            sx={{ fontSize: 10, fontWeight: 700, color: 'text.secondary', ...ELLIPSIS_SX }}
          >
            {tag.tag}
          </Typography>
        </Tooltip>
      </Box>
      <Box
        sx={(theme) => ({
          flex: 1,
          height: 6,
          borderRadius: `${theme.radius.pill}px`,
          backgroundColor: 'border.default',
          position: 'relative',
        })}
      >
        {tag.spans.map((span) => {
          const { startSec, endSec } = clampSpan(span.startSec, span.endSec, durationSeconds);
          const duration = span.endSec - span.startSec;
          return (
            <Tooltip
              key={`${startSec}-${endSec}`}
              title={`${span.startSec}s – ${span.endSec}s (${duration}s)`}
              placement="top"
            >
              <Box
                data-testid="tag-span"
                style={{
                  left: `${(startSec / durationSeconds) * 100}%`,
                  width: `${((endSec - startSec) / durationSeconds) * 100}%`,
                }}
                sx={(theme) => ({
                  position: 'absolute',
                  height: '100%',
                  borderRadius: `${theme.radius.pill}px`,
                  backgroundColor: accent,
                })}
              />
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}

export function TagTimeline({ durationSeconds, categories }: TagTimelineProps): ReactNode {
  const theme = useTheme();
  const spannedCategories = timelineCategories(categories);

  if (spannedCategories.length === 0) return null;

  return (
    <ChartSection variant="card" title="Timeline">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {spannedCategories.map((category) => {
          const accent =
            theme.palette.elementCategory[category.category] ??
            theme.palette.elementCategory.default;
          return (
            <Box key={category.category} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <CategoryLabel category={category.category} />
              {category.features.map((feature) =>
                feature.tags.map((tag) => (
                  <TagRow
                    key={`${category.category}-${feature.feature}-${tag.tag}`}
                    feature={feature}
                    tag={tag}
                    accent={accent}
                    durationSeconds={durationSeconds}
                  />
                )),
              )}
            </Box>
          );
        })}
        <Box
          sx={(theme) => ({
            display: 'flex',
            justifyContent: 'space-between',
            pl: `${TRACK_LEFT_WIDTH}px`,
            ml: theme.spacing(2),
          })}
        >
          <Tooltip title="0 seconds" placement="top">
            <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>0s</Typography>
          </Tooltip>
          <Tooltip title={`${durationSeconds} seconds`} placement="top">
            <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
              {durationSeconds}s
            </Typography>
          </Tooltip>
        </Box>
      </Box>
    </ChartSection>
  );
}
